import { Droplets } from 'lucide-react';
import { apiPaths, type WaterResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import type { WaterConformity, WaterQuality } from '../../../shared/domain/index.ts';
import { PageContainer } from '../../components/layout/PageContainer.tsx';
import { SectionCard } from '../../components/SectionCard.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatDay } from '../../lib/format.ts';

const CONFORMITY: Readonly<Record<WaterConformity, { label: string; tone: 'ok' | 'danger' | 'muted' }>> = {
  compliant: { label: 'Conforme', tone: 'ok' },
  non_compliant: { label: 'Non conforme', tone: 'danger' },
  unknown: { label: 'Conclusion non publiée', tone: 'muted' },
};

function Analysis({ water }: { water: WaterQuality }) {
  const { label, tone } = CONFORMITY[water.conformity];
  return (
    <>
      <p className="lead">
        <span className={`status-dot status-dot--${tone}`} aria-hidden="true" />
        <strong>{label}</strong> · prélèvement du <time dateTime={water.sampledAt}>{formatDay(water.sampledAt)}</time>
      </p>
      <p>{water.conclusion}</p>
      {water.measures.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Paramètre</th>
              <th scope="col">Valeur</th>
              <th scope="col">Limite réglementaire</th>
            </tr>
          </thead>
          <tbody>
            {water.measures.map((measure) => (
              <tr key={measure.label}>
                <th scope="row">{measure.label}</th>
                <td>
                  {measure.value}
                  {measure.unit === undefined ? '' : ` ${measure.unit}`}
                </td>
                <td>{measure.limit ?? 'non publiée'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(water.distributor !== undefined || water.network !== undefined) && (
        <p className="muted">
          {water.network === undefined ? '' : `Réseau : ${water.network}. `}
          {water.distributor === undefined ? '' : `Distributeur : ${water.distributor}.`}
        </p>
      )}
    </>
  );
}

/** Page dédiée à l'eau du robinet : la dernière analyse, et ce qu'elle veut dire. */
export function WaterPage() {
  const water = useApi<WaterResponse>(apiPaths.water(DEFAULT_CITY.slug));
  return (
    <PageContainer path="/eau">
      <SectionCard
        title="Dernière analyse publiée"
        icon={<Droplets size={20} aria-hidden="true" />}
        kind="water"
        section={water.data?.water}
        loading={water.loading}
        fetchError={water.error}
        subject="Les résultats du contrôle sanitaire"
      >
        {(data) => <Analysis water={data} />}
      </SectionCard>

      <section className="card" aria-labelledby="water-what">
        <h2 id="water-what">D’où vient ce résultat</h2>
        <p>
          L’eau distribuée à Ermont fait l’objet d’un <strong>contrôle sanitaire</strong> organisé par l’Agence
          régionale de santé. Des prélèvements sont effectués sur le réseau, analysés en laboratoire, puis publiés
          commune par commune. Ermont Live reprend le résultat le plus récent tel qu’il est publié, sans le
          réinterpréter, et affiche toujours la date du prélèvement.
        </p>
        <p>
          <strong>Ce n’est pas une mesure en direct.</strong> Les prélèvements sont espacés de plusieurs semaines et
          leur publication prend du temps : le résultat affiché peut donc dater de plusieurs mois. C’est la nature même
          de ce contrôle, pas un défaut de mise à jour.
        </p>
      </section>

      <section className="card" aria-labelledby="water-params">
        <h2 id="water-params">Ce que mesurent les paramètres affichés</h2>
        <ul>
          <li>
            <strong>Nitrates</strong> : proviennent surtout des activités agricoles. La limite réglementaire est de 50
            mg/L.
          </li>
          <li>
            <strong>Dureté</strong> : la teneur en calcium et magnésium, exprimée en degrés français (°f). Elle ne
            relève pas de la sécurité sanitaire : une eau dure entartre, elle n’est pas dangereuse.
          </li>
          <li>
            <strong>Chlore libre</strong> : le désinfectant qui protège l’eau tout au long du réseau. C’est lui qui
            donne parfois un goût au robinet ; il s’évapore si l’eau repose quelques minutes.
          </li>
          <li>
            <strong>pH</strong> : l’acidité de l’eau, surveillée pour la stabilité du réseau.
          </li>
        </ul>
        <p className="muted">
          Les valeurs et leurs limites sont reprises telles que publiées par le contrôle sanitaire. En cas de doute sur
          la qualité de l’eau à une adresse précise, la source officielle et le distributeur font foi.
        </p>
      </section>
    </PageContainer>
  );
}
